/* eslint-disable react/no-unescaped-entities */
import React, { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { SEO, serviceSchema, breadcrumbSchema } from "@/components/SEO";
import { Section, SpecLabel } from "@/components/Section";
import { BookACallButton } from "@/components/BookACallButton";
import { SystemIcon } from "@/components/SystemIcon";
import { Reveal } from "@/components/Reveal";
import { VideoPlaceholder } from "@/components/VideoPlaceholder";
import { SYSTEMS, getSystemBySlug } from "@/data/systems";

export default function SystemDetailPage() {
    const { slug } = useParams();
    const system = getSystemBySlug(slug);

    // Memoize page-specific JSON-LD schemas so the SEO component receives a
    // stable array reference between renders. Re-computed only when the
    // system slug changes (i.e., user navigates to a different system page).
    const seoSchemas = useMemo(() => {
        if (!system) return [];
        return [
            serviceSchema({
                name: system.name,
                description: system.shortDescription,
                slug: system.slug,
            }),
            breadcrumbSchema([
                { name: "Home", path: "/" },
                { name: system.name, path: `/systems/${system.slug}` },
            ]),
        ];
    }, [system]);

    if (!system) {
        return <Navigate to="/" replace />;
    }

    const currentIdx = SYSTEMS.findIndex((s) => s.slug === slug);
    const nextSystem = SYSTEMS[(currentIdx + 1) % SYSTEMS.length];

    return (
        <>
            <SEO
                title={`${system.name} — Marketing for Home Service Businesses`}
                description={`${system.shortDescription} Built for plumbers, HVAC contractors, roofers, electricians, and home service pros across the U.S.`}
                path={`/systems/${system.slug}`}
                schemas={seoSchemas}
            />

            {/* Back link */}
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 sm:pt-6">
                <Link
                    to="/"
                    data-testid="system-back-link"
                    className="inline-flex items-center gap-2 text-[11px] font-display font-bold uppercase tracking-[0.18em] text-[#4a5562] hover:text-[#188bf6] transition-colors"
                >
                    <ArrowLeft className="w-3.5 h-3.5" /> Back home
                </Link>
            </div>

            {/* HERO */}
            <section
                className="relative overflow-hidden bg-white"
                data-testid={`system-hero-${system.slug}`}
            >
                <div
                    className="absolute inset-0 blueprint-grid opacity-40 pointer-events-none"
                    aria-hidden="true"
                />
                <div
                    className="absolute inset-0 hero-edge-glow pointer-events-none"
                    aria-hidden="true"
                />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12 sm:pt-12 sm:pb-16">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                        <div className="lg:col-span-8">
                            <div className="inline-flex items-center gap-2 rounded-full bg-[#188bf6]/8 border border-[#188bf6]/25 px-3 py-1.5">
                                <SystemIcon
                                    slug={system.slug}
                                    className="w-3.5 h-3.5 text-[#188bf6]"
                                />
                                <span className="text-[10px] sm:text-[11px] font-display font-bold uppercase tracking-[0.18em] text-[#188bf6]">
                                    {system.order} / System
                                </span>
                            </div>
                            <h1 className="mt-3 sm:mt-4 font-display font-extrabold tracking-tight text-[#0a0b0d] text-[32px] leading-[1.06] sm:text-5xl lg:text-[56px] lg:leading-[1.04]">
                                {system.name}
                            </h1>
                            <p className="mt-4 sm:mt-6 text-base sm:text-lg lg:text-xl text-[#4a5562] max-w-2xl leading-relaxed">
                                {system.tagline}
                            </p>
                            <div className="mt-6 sm:mt-8">
                                <BookACallButton
                                    size="md"
                                    testId={`system-hero-cta-${system.slug}`}
                                />
                            </div>
                        </div>
                        <div className="lg:col-span-4 hidden lg:block">
                            <img
                                src="/assets/mascot_pose5.png"
                                alt=""
                                className="w-full max-w-[280px] mx-auto h-auto"
                                loading="lazy"
                                aria-hidden="true"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* VIDEO PLACEHOLDER — smaller, slow blue pulse border, coming soon */}
            <section
                className="bg-[#F7F8FA] py-12 sm:py-16"
                data-testid={`system-video-section-${system.slug}`}
            >
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <VideoPlaceholder systemName={system.name} />
                    </Reveal>
                </div>
            </section>

            {/* WHAT IT DOES */}
            <Section testId={`system-what-${system.slug}`}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                    <div className="lg:col-span-5">
                        <SpecLabel>What this system does</SpecLabel>
                        <h2 className="mt-3 sm:mt-4 font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-[#0a0b0d]">
                            Why it matters
                            <br /> to a home service business.
                        </h2>
                    </div>
                    <div className="lg:col-span-7">
                        <ul className="space-y-3 sm:space-y-4">
                            {system.whatItDoes.map((line) => (
                                <li
                                    key={line}
                                    className="flex items-start gap-3 text-[15px] sm:text-base lg:text-lg text-[#0a0b0d] leading-relaxed"
                                >
                                    <Check
                                        className="w-5 h-5 mt-1 text-[#188bf6] shrink-0"
                                        aria-hidden="true"
                                    />
                                    <span>{line}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </Section>

            {/* HOW IT WORKS */}
            <Section tone="surface" testId={`system-how-${system.slug}`}>
                <div className="max-w-3xl">
                    <SpecLabel>How it works</SpecLabel>
                    <h2 className="mt-3 sm:mt-4 font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-[#0a0b0d]">
                        Three steps. No mystery.
                    </h2>
                </div>
                <div className="mt-8 sm:mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                    {system.howItWorks.map((step, i) => (
                        <Reveal key={step.label} delay={i * 0.06}>
                            <div className="relative h-full rounded-2xl border-2 border-[#0a0b0d]/14 bg-white p-5 sm:p-7 card-lift">
                                <span
                                    className="absolute inset-x-0 top-0 h-1 bg-[#188bf6]"
                                    aria-hidden="true"
                                />
                                <div className="text-[11px] font-display font-bold uppercase tracking-[0.18em] text-[#188bf6]">
                                    {step.label}
                                </div>
                                <h3 className="mt-3 font-display text-lg sm:text-xl font-extrabold text-[#0a0b0d]">
                                    {step.title}
                                </h3>
                                <p className="mt-2.5 sm:mt-3 text-sm text-[#4a5562] leading-relaxed">
                                    {step.desc}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </Section>

            {/* CLOSING CTA */}
            <section
                className="relative bg-[#0a0b0d] text-white"
                data-testid={`system-final-cta-${system.slug}`}
            >
                <div className="h-1 caution-stripe" aria-hidden="true" />
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                        <div className="lg:col-span-8">
                            <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                                Run this system with the rest of the stack.
                            </h2>
                            <p className="mt-3 sm:mt-4 text-sm sm:text-base text-white/72 max-w-xl">
                                You don't buy this one piece-meal. It comes
                                with the other six on the Foundational
                                Advanced plan.
                            </p>
                            <div className="mt-6 sm:mt-7 flex flex-col sm:flex-row gap-3">
                                <BookACallButton
                                    size="md"
                                    testId={`system-final-book-a-call-${system.slug}`}
                                />
                                <Link
                                    to={`/systems/${nextSystem.slug}`}
                                    data-testid={`system-next-link-${system.slug}`}
                                    className="inline-flex items-center justify-center gap-2 h-11 sm:h-12 px-5 text-sm font-display font-bold text-white border-2 border-white/20 hover:border-[#188bf6] hover:text-[#188bf6] rounded-xl transition-colors"
                                >
                                    Next: {nextSystem.name}
                                    <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
